import axiosInstance from "../axios/axiosInstance";
import { toast } from "react-toastify";
import { t } from "i18next";

/**
 * Centralized API Service
 * Provides a clean interface for all API calls with consistent error handling
 */
class ApiService {
  /**
   * Generic GET request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config (params, headers, etc.)
   * @returns {Promise} - Response data
   */
  async get(url, config = {}) {
    try {
      const response = await axiosInstance.get(url, config);
      
      // Handle API-level status codes
      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        return response.data.response || response.data;
      }
      
      // If response doesn't have expected structure, return as is
      return response.data;
    } catch (error) {
      // Error is already handled by interceptor, but we can add component-specific handling here
      throw this.handleError(error);
    }
  }

  /**
   * Generic POST request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} - Response data
   */
  async post(url, data = {}, config = {}) {
    try {
      const response = await axiosInstance.post(url, data, config);
      
      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        return response.data.response || response.data;
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PUT request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} - Response data
   */
  async put(url, data = {}, config = {}) {
    try {
      const response = await axiosInstance.put(url, data, config);
      
      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        return response.data.response || response.data;
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PATCH request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} - Response data
   */
  async patch(url, data = {}, config = {}) {
    try {
      const response = await axiosInstance.patch(url, data, config);
      
      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        return response.data.response || response.data;
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic DELETE request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config
   * @returns {Promise} - Response data
   */
  async delete(url, config = {}) {
    try {
      const response = await axiosInstance.delete(url, config);
      
      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        return response.data.response || response.data;
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle errors consistently
   * @param {Error} error - Error object
   * @returns {Error} - Formatted error
   */
  handleError(error) {
    // Error is already handled by interceptor for common cases
    // This method allows for additional custom handling if needed
    return error;
  }

  /**
   * Show error toast with optional custom message
   * @param {string} defaultMessageKey - i18n key for default error message
   * @param {string} customMessage - Optional custom message
   */
  showError(defaultMessageKey, customMessage = null) {
    toast.dismiss();
    toast.error(customMessage || t(defaultMessageKey), {
      autoClose: false,
    });
  }
}

// Export singleton instance
export default new ApiService();